import { useQuery } from '@tanstack/react-query';
import { FinancingApi, Disclosure } from '@servicetitan/financing-api-client';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import { useDependencies } from '@servicetitan/react-ioc';

export interface UseDynamicDisclosuresOptions {
    /** Optional type of disclosure, such as "solar" */
    type?: string;
    /** Optional business unit ID if separate financing account */
    buId?: string;
    /** Whether to enable the query */
    enabled?: boolean;
}

export interface DynamicDisclosuresData {
    disclosures: Disclosure[];
    token: string | null;
    tokenExpiration: string;
}

/**
 * Hook to fetch dynamic disclosures from the API
 */
export const useDynamicDisclosures = (options: UseDynamicDisclosuresOptions = {}) => {
    const { setupData } = SetupDataStore.useStore();
    const [financingApi] = useDependencies(FinancingApi);

    const { type, buId, enabled = true } = options;

    return useQuery({
        queryKey: ['dynamic-disclosures', setupData?.provider, setupData?.tenantId, type, buId],
        queryFn: async (): Promise<DynamicDisclosuresData> => {
            if (!setupData?.provider || !setupData?.tenantId) {
                throw new Error('Provider and tenant ID are required');
            }

            const response = await financingApi.legalDisclosuresGetLegalDisclosures({
                providerName: setupData.provider,
                tenantId: setupData.tenantId,
                type,
                buId: buId ?? setupData.buId,
            });

            return {
                disclosures: response.data.disclosures ?? [],
                token: response.data.token ?? null,
                tokenExpiration: response.data.tokenExpiration,
            };
        },
        enabled: enabled && !!setupData?.provider && !!setupData?.tenantId,
    });
};
